import { create } from 'zustand';
import type { IdpEnv } from '@/shared/auth/idp';

/**
 * Auth state.
 *
 * The session lives in the HttpOnly `ism_portal` cookie set by the central IdP;
 * the edge exchanges it for the backend `Token` header per request. Nothing
 * secret reaches this store and nothing is persisted — boot always asks the
 * server who we are (`shared/auth/api.ts#bootstrapSession`). That costs one
 * request behind the boot splash and removes the class of bugs where a stale
 * localStorage session disagreed with the server about the current database.
 */

export interface AuthUser {
  userId: number;
  userGuid: string;
  login: string;
  userName: string | null;
  email?: string | null;
  distrId: number | null;
  clientId: number | null;
  defaultPage: string | null;
  defaultPageDistr?: string | null;
  defaultPageClient?: string | null;
  isTemporary?: boolean;
  osmSettings?: string | null;
  portalAccess: string[];
  /**
   * `SQLStorageServer.SQLStorageDatabase` of the session — the per-client
   * FILESTREAM database used by the file upload/download endpoints.  Resolved
   * server-side, kept here only so the UI can tell which storage it is on.
   */
  storageDatabase?: string | null;
  /**
   * Databases the user may work with, as the login response returns them (raw
   * rows — `toDistributors` in `shared/auth/distributors.ts` gives them a
   * shape).
   *
   * Kept so the nav bar can name the current database without waiting for
   * `SP_Common/getUserClients`; the legacy portal labels its switcher from the
   * same array.  It is a narrower row than `getUserClients` (no colour, no cube
   * server, active databases only), so treat it as a placeholder, not a source
   * of truth.
   */
  distributors?: unknown;
  userSettings?: unknown;
}

/**
 * Which environment (contour) the session is bound to, and which others the
 * user may switch to. Distinct from the distributor: one environment holds many
 * distributors, and switching environment re-issues the cookie while switching
 * distributor only mutates the backend token.
 */
export interface AuthSession {
  login: string;
  vaultId: string;
  envName: string;
  role: number | null;
  envs: IdpEnv[];
}

interface AuthState {
  user: AuthUser | null;
  session: AuthSession | null;
  bootstrapped: boolean;

  setUser: (user: AuthUser) => void;
  setSession: (session: AuthSession | null) => void;
  setBootstrapped: (v: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  session: null,
  bootstrapped: false,

  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setBootstrapped: (v) => set({ bootstrapped: v }),
  clear: () => set({ user: null, session: null }),
}));
